import { mediaQueryLarge, isMobileBreakpoint, prefersReducedMotion } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /**
   * Duration and easing mirror the theme's `--animation-speed-slow` / `--animation-easing`
   * design tokens (see theme-styles-variables.liquid). Kept as JS constants rather than read
   * from the custom properties at runtime to avoid parsing CSS <time> values.
   */
  static #ANIMATION_DURATION = 200;
  static #ANIMATION_EASING = 'ease-in-out';

  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  /** @type {HTMLElement | null} */
  get #content() {
    return this.details.querySelector(':scope > .details-content');
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #disableAnimationOnMobile() {
    return this.dataset.disableAnimationOnMobile === 'true';
  }

  get #disableAnimationOnDesktop() {
    return this.dataset.disableAnimationOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /** @type {boolean} Whether an open/close animation should run for the current breakpoint. */
  get #shouldAnimate() {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    return (
      !prefersReducedMotion() &&
      !((isMobile && this.#disableAnimationOnMobile) || (isDesktop && this.#disableAnimationOnDesktop))
    );
  }

  /** @type {Animation | null} */
  #animation = null;

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Take over the toggle behaviour so we control exactly when the `open` attribute
    // changes, which lets the closing animation finish before the content is hidden.
    event.preventDefault();

    if (this.details.open) {
      this.#collapse(this.#shouldAnimate);
    } else {
      this.#expand(this.#shouldAnimate);
    }
  };

  /**
   * Opens the accordion, animating the content from 0 to its natural height unless animation is disabled.
   * The `open` attribute is set immediately so the content renders and can be measured.
   * @param {boolean} animate
   */
  #expand(animate) {
    const details = this.details;
    const content = this.#content;

    this.#animation?.cancel();
    this.#resetContentStyles();

    if (!animate || !content) {
      details.open = true;
      return;
    }

    details.open = true;

    const endHeight = content.scrollHeight;
    const { paddingTop, paddingBottom } = getComputedStyle(content);

    content.style.overflow = 'hidden';

    this.#animation = content.animate(
      {
        height: ['0px', `${endHeight}px`],
        paddingTop: ['0px', paddingTop],
        paddingBottom: ['0px', paddingBottom],
        opacity: [0, 1],
      },
      {
        duration: AccordionCustom.#ANIMATION_DURATION,
        easing: AccordionCustom.#ANIMATION_EASING,
      }
    );

    this.#animation.onfinish = () => {
      this.#animation = null;
      this.#resetContentStyles();
    };
  }

  /**
   * Closes the accordion, animating the content down to 0 unless animation is disabled.
   * The `open` attribute is only removed once the closing animation has finished, so the
   * content stays visible (and measurable) for the full duration of the animation.
   * @param {boolean} animate
   */
  #collapse(animate) {
    const details = this.details;
    const content = this.#content;

    this.#animation?.cancel();
    this.#resetContentStyles();

    if (!animate || !content) {
      details.open = false;
      return;
    }

    const startHeight = content.scrollHeight;
    const { paddingTop, paddingBottom } = getComputedStyle(content);

    content.style.overflow = 'hidden';

    this.#animation = content.animate(
      {
        height: [`${startHeight}px`, '0px'],
        paddingTop: [paddingTop, '0px'],
        paddingBottom: [paddingBottom, '0px'],
        opacity: [1, 0],
      },
      {
        duration: AccordionCustom.#ANIMATION_DURATION,
        easing: AccordionCustom.#ANIMATION_EASING,
      }
    );

    this.#animation.onfinish = () => {
      this.#animation = null;
      details.open = false;
      this.#resetContentStyles();
    };
  }

  /**
   * Clears the inline styles applied while an open/close animation is running.
   */
  #resetContentStyles() {
    const content = this.#content;

    if (content) content.style.overflow = '';
  }

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    // Breakpoint-driven state changes are never animated, so cancel any animation left
    // running from a direct user interaction before snapping to the new state.
    this.#animation?.cancel();
    this.#resetContentStyles();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      if (this.details.open) {
        this.#collapse(this.#shouldAnimate);
      }

      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
